<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import ProgressSpinner from 'primevue/progressspinner'
import BrowseGoalCard from '@/goals/BrowseGoalCard.vue'
import StrategyCard from '@/library/StrategyCard.vue'
import RecentlyViewedResourceCard from '@/home/RecentlyViewedResourceCard.vue'
import { getRecentlyViewedContent } from '@/library/api'
import { useUserStore } from '@/authentication/user.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { getGoalSubjectLabelFromValue } from '@/contentprofile/no_sel_composable'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { isBetaOptIn, withBetaOptIn } from '@/util/beta_opt_in'
import { announce, clearAnnouncement } from '@/util/live_announcer'
import { goalHitFromViewedGoal, recentlyViewedPageUrl, RECENTLY_VIEWED_TABS } from '@/home/recently_viewed'
import type { RecentlyViewedTab } from '@/home/recently_viewed'
import { RECENTLY_VIEWED_EVENT_NAMES } from '@/home/recently_viewed_event_names'
import type { GoalHit } from '@/goals/goal_types'
import type { LibraryContentObject, LibraryGoal } from '@/library/library_types'

type LoadResult = {
  content: LibraryContentObject[]
  hasError: boolean
}

const CARD_HEADING_LEVEL = 4
const SELECTED_TAB_CLASSES = 'bg-blue-400 text-white focus-visible:outline-blue-400'
// The resting blue on the hover grey falls below 4.5:1 contrast, so hover and focus darken the text.
const UNSELECTED_TAB_CLASSES =
  'bg-white text-blue-400 hover:bg-gray-300 hover:text-blue-500 focus-visible:bg-gray-300 focus-visible:text-blue-500 focus-visible:outline-gray-700'

const analytics = getAnalyticsTracker().getChildContext({ ref: 'home' })
const { userId } = storeToRefs(useUserStore())
const { betaVisibility } = storeToRefs(useVisibilityStore())
const contentProfileStore = useContentProfileStore()
const route = useRoute()

const selectedTab = ref<RecentlyViewedTab>(RECENTLY_VIEWED_TABS[0])
const viewedContent = ref<LibraryContentObject[]>([])
const isLoading = ref(true)
const hasLoadError = ref(false)

const hasViewedContent = computed(() => viewedContent.value.length > 0)
const hasLoaded = computed(() => !isLoading.value && !hasLoadError.value)
const isContentShown = computed(() => hasLoaded.value && hasViewedContent.value)
const isEmptyStateShown = computed(() => hasLoaded.value && !hasViewedContent.value)
const isErrorShown = computed(() => !isLoading.value && hasLoadError.value)

const viewedGoals = computed(() =>
  viewedContent.value.map((viewed) => goalHitFromViewedGoal(viewed.content_body as LibraryGoal))
)

// Home is served only behind ?beta=true, so a link that drops it lands the viewer back on Vue 2 pages.
const isBetaKept = computed(() => betaVisibility.value || isBetaOptIn(route.query))
function keepBeta(url: string): string {
  if (!isBetaKept.value) {
    return url
  }
  return withBetaOptIn(url)
}
const viewMoreUrl = computed(() => keepBeta(recentlyViewedPageUrl(selectedTab.value.contentType)))
const browseUrl = computed(() => keepBeta(selectedTab.value.browseUrl))

function goalSubjectLabel(goal: GoalHit): string {
  return getGoalSubjectLabelFromValue(goal.primary_subject)
}
function standardSetLabelFor(goal: GoalHit): string {
  return contentProfileStore.standardSetLabelForSubjectCard(goalSubjectLabel(goal))
}
function standardSetForRefDisplayFor(goal: GoalHit): string {
  return contentProfileStore.dropdownStandardSetForSubject(goalSubjectLabel(goal))
}

function isTabSelected(tab: RecentlyViewedTab): boolean {
  return tab.contentType === selectedTab.value.contentType
}

function tabClasses(tab: RecentlyViewedTab): string {
  if (isTabSelected(tab)) {
    return SELECTED_TAB_CLASSES
  }
  return UNSELECTED_TAB_CLASSES
}

function selectTab(tab: RecentlyViewedTab) {
  selectedTab.value = tab
  analytics.sendEvent(RECENTLY_VIEWED_EVENT_NAMES.SELECTED_TAB[tab.contentType])
}

function recordViewMoreClick() {
  analytics.sendEvent(RECENTLY_VIEWED_EVENT_NAMES.CLICKED_VIEW_MORE[selectedTab.value.contentType])
}

function recordBrowseClick() {
  analytics.sendEvent(RECENTLY_VIEWED_EVENT_NAMES.CLICKED_BROWSE[selectedTab.value.contentType])
}

// A deleted or unpublished item comes back with a content_body_error in place of its content_body.
function isStillAvailable(viewed: LibraryContentObject): boolean {
  return Boolean(viewed.content_body)
}

function loadErrorMessage(tab: RecentlyViewedTab): string {
  return `We're not able to retrieve ${tab.contentType} right now. Try again later.`
}

async function fetchViewedContent(tab: RecentlyViewedTab): Promise<LoadResult> {
  try {
    const response = await getRecentlyViewedContent(userId.value, tab.apiContentType, tab.limit)
    const viewedItems = response.content ?? []
    return { content: viewedItems.filter(isStillAvailable), hasError: false }
  } catch (error) {
    console.error(`Error fetching recently viewed ${tab.contentType}:`, error)
    return { content: [], hasError: true }
  }
}

let latestLoadId = 0

// A response that arrives after a newer load started belongs to a tab or user the viewer has left.
async function loadSelectedTab() {
  const tab = selectedTab.value
  const loadId = ++latestLoadId
  isLoading.value = true
  hasLoadError.value = false
  viewedContent.value = []
  announce(`Loading ${tab.contentType}...`)
  const result = await fetchViewedContent(tab)
  if (loadId !== latestLoadId) {
    return
  }
  viewedContent.value = result.content
  hasLoadError.value = result.hasError
  clearAnnouncement()
  isLoading.value = false
  if (result.hasError) {
    announce(loadErrorMessage(tab))
  }
}

watch(
  [userId, selectedTab],
  () => {
    if (!userId.value) {
      return
    }
    loadSelectedTab()
  },
  { immediate: true }
)
</script>

<template>
  <div>
    <h3 class="mb-2.5 mt-5 text-display-sm font-medium leading-[1.1]">Recently Viewed</h3>
    <div class="mb-4 mt-3 flex gap-5">
      <button
        v-for="tab in RECENTLY_VIEWED_TABS"
        :key="tab.contentType"
        type="button"
        class="rounded-xs px-4 py-2 text-center text-md focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        :class="tabClasses(tab)"
        :aria-pressed="isTabSelected(tab)"
        :aria-label="tab.label"
        :data-testid="`recently-viewed-tab-${tab.contentType}`"
        @click="selectTab(tab)"
      >
        {{ tab.label }}
      </button>
    </div>

    <div v-if="isLoading" role="status" aria-live="polite" class="inline-flex items-center text-gray-700">
      <ProgressSpinner strokeWidth="8" class="h-5 w-5 stroke-primary-400" aria-hidden="true" />
      <span class="ml-2">Loading {{ selectedTab.contentType }}...</span>
    </div>

    <div v-if="isErrorShown" class="flex w-fit items-center rounded-xs bg-blue-200 p-2 text-gray-900">
      <span class="material-icons-outlined mr-2 text-blue-400" aria-hidden="true">info</span>
      {{ loadErrorMessage(selectedTab) }}
    </div>

    <div v-if="isContentShown">
      <div class="fs-exclude" data-testid="recently-viewed-content">
        <div v-if="selectedTab.contentType === 'goals'" class="my-4 grid grid-cols-1 gap-4 sm:grid-cols-[repeat(2,277px)]">
          <BrowseGoalCard
            v-for="(goal, index) in viewedGoals"
            :key="goal.uuid"
            :goal="goal"
            :position="index"
            :heading-level="CARD_HEADING_LEVEL"
            :standard-set-label="standardSetLabelFor(goal)"
            :standard-set-for-ref-display="standardSetForRefDisplayFor(goal)"
          />
        </div>
        <div v-if="selectedTab.contentType === 'strategies'" class="grid grid-cols-1 gap-4 sm:grid-cols-[420px]">
          <StrategyCard
            v-for="strategy in viewedContent"
            :key="strategy.content_uuid"
            :strategy="strategy"
            :removable="false"
            :heading-level="CARD_HEADING_LEVEL"
            :is-updated-time-shown="false"
          />
        </div>
        <template v-if="selectedTab.contentType === 'resources'">
          <RecentlyViewedResourceCard
            v-for="(resource, index) in viewedContent"
            :key="resource.content_uuid"
            :position="index + 1"
            v-bind="resource.content_body"
            :num_pages="0"
            :show-resource-type="false"
            :omit-description="true"
            :heading-level="CARD_HEADING_LEVEL"
          />
        </template>
      </div>
      <a :href="viewMoreUrl" class="text-blue-400 hover:text-blue-500 hover:underline" @click="recordViewMoreClick">
        View more {{ selectedTab.contentType }}
      </a>
    </div>

    <div v-if="isEmptyStateShown" class="text-xl italic text-gray-900">
      <p>You haven't viewed any {{ selectedTab.contentType }} yet</p>
      <a :href="browseUrl" class="font-medium not-italic text-blue-400 hover:underline" @click="recordBrowseClick">
        Browse {{ selectedTab.browseLabel }}
      </a>
    </div>
  </div>
</template>
